import moment from 'moment-timezone'
import { registerModule } from './safeEval';

/**
 * Wrapper class for a date object.
 * Currently uses moment-timezone internally
 */
class CDate {
    #internalMoment;
    constructor(date, tz) {
        date = date instanceof CDate ?  date.#internalMoment : date;
        if (tz) {
            this.#internalMoment = moment.tz(date, tz);
        } else {
            this.#internalMoment = moment(date);
        }
    }
    clone() {
        return new CDate(this.#internalMoment.toDate(), this.#internalMoment.tz());
    }

    //#region manipulation functions
    date(date) {
        return this.#internalMoment.date(date);
    }
    day(day) {
        return this.#internalMoment.day(day);
    }
    week(week) {
        return this.#internalMoment.week(week);
    }
    month(month) {
        return this.#internalMoment.month(month);
    }
    weekday(weekday) {
        return this.#internalMoment.weekday(weekday);
    }
    isoWeekday(weekday) {
        return this.#internalMoment.isoWeekday(weekday);
    }
    hours(hours) {
        if (hours !== undefined) {
           return new CDate(moment(this.#internalMoment).hours(hours))
        }
        return this.#internalMoment.hours();
    }
    minutes(minutes) {
        if (minutes !== undefined) {
            return new CDate(moment(this.#internalMoment).minutes(minutes))
        }
        return this.#internalMoment.minutes();
    }
    seconds(seconds) {
        if (seconds !== undefined) {
            return new CDate(moment(this.#internalMoment).seconds(seconds))
        }
        return this.#internalMoment.seconds();
    }
    milliseconds(quantity) {
        if (quantity !== undefined) {
            return new CDate(moment(this.#internalMoment).milliseconds(quantity))
        }
        return this.#internalMoment.milliseconds();
    }
    setTimeElements(hours = 0, minutes = 0, seconds = 0, milliseconds = 0){
        return this.hours(hours).minutes(minutes).seconds(seconds).milliseconds(milliseconds);
    }
    applyTime(cdateTime) {
        if (!(cdateTime instanceof CDate)) {
            throw new Error("Time must be provided as a CDate instance");
        }

        this.#internalMoment.set({
            hour: cdateTime.#internalMoment.hour(),
            minute: cdateTime.#internalMoment.minute(),
            second: cdateTime.#internalMoment.second(),
            millisecond: cdateTime.#internalMoment.millisecond(),
        });

        return this;
    }
    //#endregion

    //#region calculation functions
    add(quantity, units) {
        return new CDate(moment(this.#internalMoment).add(quantity, units));
    }

    subtract(quantity, units) {
        return new CDate(moment(this.#internalMoment).subtract(quantity, units));
    }

    diff(date, units, precision) {
        if (!(date instanceof CDate)) {
            throw new Error("Differences can only be computed against other CDates");
        }
        return this.#internalMoment.diff(date.#internalMoment, units, precision);
    }

    startOf(unit) {
        return new CDate(moment(this.#internalMoment).startOf(unit));
    }

    endOf(unit) {
        return new CDate(moment(this.#internalMoment).endOf(unit));
    }

    duration(end) {
        return new CDuration(this, end);
    }
    //#endregion

    //#region query functions
    isBefore(date) {
        if (!(date instanceof CDate)) {
            throw new Error("A CDate can only be compared to another CDate");
        }
        return this.#internalMoment.isBefore(date.#internalMoment);
    }

    isAfter(date) {
        if (!(date instanceof CDate)) {
            throw new Error("A CDate can only be compared to another CDate");
        }
        return this.#internalMoment.isAfter(date.#internalMoment);
    }
    
    isSameOrBefore(date) {
        if (!(date instanceof CDate)) {
            throw new Error("A CDate can only be compared to another CDate");
        }
        return this.#internalMoment.isSameOrBefore(date.#internalMoment);
    }

    isSameOrAfter(date) {
        if (!(date instanceof CDate)) {
            throw new Error("A CDate can only be compared to another CDate");
        }
        return this.#internalMoment.isSameOrAfter(date.#internalMoment);
    }

    isSame(date, granularity) {
        if (!(date instanceof CDate)) {
            throw new Error("A CDate can only be compared to another CDate");
        }
        return this.#internalMoment.isSame(date.#internalMoment, granularity);
    }

    isBetween(start, end, precision, inclusivity = '()') {
        if (!(start instanceof CDate) || !(end instanceof CDate)) {
            throw new Error("A CDate can only be compared to other CDates");
        }
        if (start.isAfter(end)) {
            throw new Error("The first argument must be before the second");
        }
        return this.#internalMoment.isBetween(start.#internalMoment, end.#internalMoment, precision, inclusivity);
    }

    tz(tz, force) {
        this.#internalMoment.tz(tz, force);
        return this;
    }

    isValid() {
        return this.#internalMoment.isValid();
    }
    //#endregion

    //#region fomatting functions
    #addTimezone(formatString, showTz, longFormat) {
        let tzFormat = longFormat ? ' (Z z)' : ' z';
        return `${formatString}${showTz ? tzFormat : ''}`;
    }

    dateFormat(showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone('D-MMM-YYYY', showTz, longFormat)); 
    }

    timeFormat(showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone('HH:mm', showTz, longFormat));
    }

    timeWithSecondsFormat(showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone('HH:mm:ss', showTz, longFormat));
    }

    dateTimeFormat(showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone('D-MMM-YYYY HH:mm', showTz, longFormat))
    }

    dateTimeNoYearFormat(showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone('D-MMM HH:mm', showTz, longFormat))
    }

    dateTimeWithSecondsFormat(showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone('D-MMM-YYYY HH:mm:ss', showTz, longFormat))
    }

    longDateFormat(showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone('LL', showTz, longFormat));
    }

    dayOfWeekFormat(showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone('ddd', showTz, longFormat));
    }

    customFormat(format, showTz, longFormat) {
        return this.#internalMoment.format(this.#addTimezone(format, showTz, longFormat));
    }

    timezoneFormat() {
        return this.#internalMoment.format('z');
    }

    fileName(showTz) {
        return this.#internalMoment.format(`YYYYMMDD_HHmmss${showTz ? 'z' : ''}`);
    }
    //#endregion

    toDate() {
        return this.#internalMoment.toDate();
    }

    toISOString() {
        return this.#internalMoment.clone().toISOString();
    }

    toEpoch() {
        return this.#internalMoment.valueOf();
    }

}

class CDuration {
    #internalDuration;
    constructor(start, end) {
        if (!(start instanceof CDate && end instanceof CDate)) {
            throw new Error("Durations can only be calculated with CDates");
        }
        this.#internalDuration = moment.duration(start.diff(end));
    }

    days() {
        return this.#internalDuration.days();
    }
    hours() {
        return this.#internalDuration.hours();
    }
    minutes() {
        return this.#internalDuration.minutes();
    }
    seconds() {
        return this.#internalDuration.seconds();
    }
}

export const cdate = (date, tz) => date ? new CDate(date, tz) : date;
export const now = (tz) => new CDate(undefined, tz);
export const isTimestamp = (str) => (/^(\d{4}-[01]\d-[0-3]\d[T ][0-2]\d:[0-5]\d:[0-5]\d(\.\d+)?([+-][0-2]\d:[0-5]\d|Z)?)$/).test(str);
export const parseCustomFieldDateToISO = (date, tz) => {
    const invalidDatePattern = /\b(0?[1-9]|[12]\d|3[01])[-./\\\s](0?[1-9]|1[0-2])[-./\\\s](\d{2}|\d{4})\b/;
    return invalidDatePattern.test(date) ? moment.tz(date, ["DD-MM-YY", "DD-MM-YYYY", "D-M-YY", "D-M-YYYY"], tz).toISOString() : date;
}

export const parseCustomFieldDateTimeToISO = (date, tz) => {
    const invalidDatePattern = /\b(0?[1-9]|[12]\d|3[01])[-./\\\s](0?[1-9]|1[0-2])[-./\\\s](\d{2}|\d{4})(?:[ T](\d{1,2}:\d{2}(?::\d{2})?)?)?\b/;
    if (invalidDatePattern.test(date)) {
        const formats = [ "DD-MM-YY", "DD-MM-YYYY", "D-M-YY", "D-M-YYYY", "DD-MM-YY HH:mm", "DD-MM-YYYY HH:mm", "D-M-YY HH:mm", "D-M-YYYY HH:mm", "DD-MM-YY HH:mm:ss", "DD-MM-YYYY HH:mm:ss", "D-M-YY HH:mm:ss", "D-M-YYYY HH:mm:ss"];
        return moment.tz(date, formats, tz).toISOString();
    }
    return date;
};

/**
 * 
 * @param  {...any} args 
 * @returns a new CDate object representing the earliest date passed in, or now if no arguments were provided
 */
export const minDate = (...args) => {
    if (args.length === 0) {
        return now();
    }
    if (args.some(d => !(d instanceof CDate) || !d.isValid())) {
        throw new Error("minDate can only be called on valid CDates");
    }
    return new CDate(args.reduce((min, cur) => min.toEpoch() > cur.toEpoch() ? cur : min, args[0]).clone());
}

import.meta.url && registerModule('cDate', () => import(/* webpackMode: "lazy" */ './CDate.js'));